import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { RootState } from '../index';

export type PlayerNotifications = {
    notifications?: any[];
    unreadCount?: number;
    categories?: any[];
    categoriesWithCounters?: any[];
    notificationsByCategory?: { [key: string]: any[]; };
    [key: string]: any; // Allow any additional fields from the API
};

interface PlayerNotificationsState {
    data: PlayerNotifications;
    loading: boolean;
    error: string | null;
    lastFetched: string | null;
}

const initialState: PlayerNotificationsState = {
    data: {
        notifications: [],
        unreadCount: 0,
        categories: [],
        categoriesWithCounters: [],
        notificationsByCategory: {}
    },
    loading: false,
    error: null,
    lastFetched: null
};

const apiUrl = window.config.playerApiUrl;

export const fetchPlayerNotifications = createAsyncThunk(
    'playerNotifications/fetchPlayerNotifications',
    async (_, { rejectWithValue, getState }) => {
        try {
            const state: RootState = getState() as RootState;

            if (state.authentication.access_token && state.authentication.auth_type === 'user') {
                const response = await axios.get<PlayerNotifications>(`${apiUrl}/player/data/notifications`, {
                    headers: {
                        Authorization: 'Bearer ' + state.authentication.access_token,
                    },
                });

                if (response.data) {
                    return { data: response.data, success: true };
                }
            }
            return rejectWithValue({
                error: 'Failed to fetch player notifications',
            });
        } catch (err: any) {
            const errResp = { error: err.toString() };
            return rejectWithValue(errResp);
        }
    }
);

const playerNotificationsSlice = createSlice({
    name: 'playerNotifications',
    initialState,
    reducers: {
        // Update notification read status
        updateNotificationReadStatus: (state, action: PayloadAction<{ notificationId: string | number; read: boolean; }>) => {
            if (state.data.notifications) {
                const { notificationId, read } = action.payload;
                const notification = state.data.notifications.find(n => n.id === notificationId);
                if (notification) {
                    const wasUnread = !notification.read;
                    const category = notification.category || 'Other';
                    notification.read = read;

                    // Update unread count
                    if (wasUnread && read && state.data.unreadCount) {
                        state.data.unreadCount = Math.max(0, state.data.unreadCount - 1);
                    } else if (!wasUnread && !read) {
                        state.data.unreadCount = (state.data.unreadCount || 0) + 1;
                    }

                    // Update categoriesWithCounters if it exists
                    if (state.data.categoriesWithCounters) {
                        state.data.categoriesWithCounters = state.data.categoriesWithCounters.map(cat => {
                            if (cat.name === category || cat.name === 'All') {
                                let newUnreadCount = cat.unreadCount;
                                if (wasUnread && read) {
                                    newUnreadCount = Math.max(0, cat.unreadCount - 1);
                                } else if (!wasUnread && !read) {
                                    newUnreadCount = cat.unreadCount + 1;
                                }
                                return {
                                    ...cat,
                                    unreadCount: newUnreadCount
                                };
                            }
                            return cat;
                        });
                    }

                    // Update notificationsByCategory if it exists
                    if (state.data.notificationsByCategory && state.data.notificationsByCategory[category]) {
                        const categoryNotification = state.data.notificationsByCategory[category].find(n => n.id === notificationId);
                        if (categoryNotification) {
                            categoryNotification.read = read;
                        }
                    }
                }
            }
        },

        // Delete notification from store
        deleteNotificationFromStore: (state, action: PayloadAction<{ notificationId: string | number; }>) => {
            if (state.data.notifications) {
                const { notificationId } = action.payload;
                const notificationIndex = state.data.notifications.findIndex(n => n.id === notificationId);
                if (notificationIndex >= 0) {
                    const notification = state.data.notifications[notificationIndex];
                    const category = notification.category || 'Other';

                    // Update unread count if the deleted notification was unread
                    if (!notification.read && state.data.unreadCount) {
                        state.data.unreadCount = Math.max(0, state.data.unreadCount - 1);
                    }

                    // Remove notification from array
                    state.data.notifications.splice(notificationIndex, 1);

                    // Update categoriesWithCounters if it exists
                    if (state.data.categoriesWithCounters) {
                        state.data.categoriesWithCounters = state.data.categoriesWithCounters.map(cat => {
                            if (cat.name === category) {
                                return {
                                    ...cat,
                                    count: Math.max(0, cat.count - 1),
                                    unreadCount: !notification.read ? Math.max(0, cat.unreadCount - 1) : cat.unreadCount
                                };
                            } else if (cat.name === 'All') {
                                return {
                                    ...cat,
                                    count: Math.max(0, cat.count - 1),
                                    unreadCount: !notification.read ? Math.max(0, cat.unreadCount - 1) : cat.unreadCount
                                };
                            }
                            return cat;
                        }).filter(cat => cat.count > 0); // Remove all empty categories, including "All"
                    }

                    // Update notificationsByCategory if it exists
                    if (state.data.notificationsByCategory && state.data.notificationsByCategory[category]) {
                        const categoryNotifications = state.data.notificationsByCategory[category];
                        const categoryNotificationIndex = categoryNotifications.findIndex(n => n.id === notificationId);
                        if (categoryNotificationIndex >= 0) {
                            categoryNotifications.splice(categoryNotificationIndex, 1);

                            // Remove category if it's empty
                            if (categoryNotifications.length === 0) {
                                delete state.data.notificationsByCategory[category];
                            }
                        }
                    }
                }
            }
        },

        addNotificationToStore: (state, action: PayloadAction<{ notification: any; }>) => {
            const { notification } = action.payload;

            if (!state.data.notifications) {
                state.data.notifications = [];
            }

            // Check if notification already exists
            const existingIndex = state.data.notifications.findIndex(n => n.id === notification.id);
            if (existingIndex >= 0) {
                // Update existing notification
                state.data.notifications[existingIndex] = notification;
                return;
            }

            // Add notification to the beginning of the array (newest first)
            state.data.notifications.unshift(notification);

            // Update unread count if the notification is unread
            if (!notification.read) {
                state.data.unreadCount = (state.data.unreadCount || 0) + 1;
            }

            // Update categoriesWithCounters if it exists
            const category = notification.category || 'Other';
            if (state.data.categoriesWithCounters) {
                state.data.categoriesWithCounters = state.data.categoriesWithCounters.map(cat => {
                    if (cat.name === category) {
                        return {
                            ...cat,
                            count: cat.count + 1,
                            unreadCount: !notification.read ? cat.unreadCount + 1 : cat.unreadCount
                        };
                    } else if (cat.name === 'All') {
                        return {
                            ...cat,
                            count: cat.count + 1,
                            unreadCount: !notification.read ? cat.unreadCount + 1 : cat.unreadCount
                        };
                    }
                    return cat;
                });

                // Add new category if it doesn't exist
                const categoryExists = state.data.categoriesWithCounters.some(cat => cat.name === category);
                if (!categoryExists && category !== 'All') {
                    state.data.categoriesWithCounters.push({
                        name: category,
                        count: 1,
                        unreadCount: !notification.read ? 1 : 0
                    });
                }
            }

            // Update notificationsByCategory if it exists
            if (state.data.notificationsByCategory) {
                if (!state.data.notificationsByCategory[category]) {
                    state.data.notificationsByCategory[category] = [];
                }
                state.data.notificationsByCategory[category].unshift(notification);
            }
        },

        // Mark all notifications as read
        markAllNotificationsAsRead: (state) => {
            if (state.data.notifications) {
                state.data.notifications.forEach(notification => {
                    notification.read = true;
                });
                state.data.unreadCount = 0;

                // Update categoriesWithCounters if it exists
                if (state.data.categoriesWithCounters) {
                    state.data.categoriesWithCounters = state.data.categoriesWithCounters.map(cat => ({
                        ...cat,
                        unreadCount: 0
                    }));
                }

                // Update notificationsByCategory if it exists
                if (state.data.notificationsByCategory) {
                    Object.keys(state.data.notificationsByCategory).forEach(category => {
                        state.data.notificationsByCategory![category].forEach(notification => {
                            notification.read = true;
                        });
                    });
                }
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPlayerNotifications.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPlayerNotifications.fulfilled, (state, action: PayloadAction<{ data: PlayerNotifications; success: boolean; }>) => {
                console.log('🎯 fetchPlayerNotifications.fulfilled - updating store with:', action.payload.data);
                console.log('📊 Previous state before API update:', {
                    notifications: state.data.notifications?.length || 0,
                    unreadCount: state.data.unreadCount,
                    categoriesWithCounters: state.data.categoriesWithCounters?.length || 0
                });

                state.loading = false;

                // Calculate the actual unread count from notifications
                const notifications = action.payload.data.notifications || [];
                const actualUnreadCount = notifications.filter(n => !n.read).length;

                // **IMPORTANT**: Check if API is returning empty data and preserve existing data if needed
                if (notifications.length === 0 && state.data.notifications && state.data.notifications.length > 0) {
                    console.log('⚠️ WARNING: API returned empty notifications but store has data - preserving existing notifications');
                    // Don't overwrite existing notifications with empty API data
                    return;
                }

                // Preserve all API response data while ensuring our required fields have defaults
                state.data = {
                    ...action.payload.data,
                    notifications: notifications,
                    unreadCount: actualUnreadCount, // Use calculated unread count instead of API value
                    categories: action.payload.data.categories || [],
                    categoriesWithCounters: action.payload.data.categoriesWithCounters || [],
                    notificationsByCategory: action.payload.data.notificationsByCategory || {}
                };
                state.lastFetched = new Date().toISOString();
                console.log('✅ Store updated successfully with:', (state.data.notifications ? state.data.notifications.length : 0), 'notifications');
            })
            .addCase(fetchPlayerNotifications.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string || 'Unknown error occurred';
            });
    }
});

// Export actions
export const {
    updateNotificationReadStatus,
    deleteNotificationFromStore,
    addNotificationToStore,
    markAllNotificationsAsRead
} = playerNotificationsSlice.actions;

// Export selectors
export const selectPlayerNotifications = (state: { playerNotifications: PlayerNotificationsState; }) => state.playerNotifications.data;
export const selectNotificationsList = (state: { playerNotifications: PlayerNotificationsState; }) => state.playerNotifications.data.notifications;
export const selectUnreadCount = (state: { playerNotifications: PlayerNotificationsState; }) => state.playerNotifications.data.unreadCount || 0;
export const selectCategories = (state: { playerNotifications: PlayerNotificationsState; }) => state.playerNotifications.data.categories;
export const selectCategoriesWithCounters = (state: { playerNotifications: PlayerNotificationsState; }) => state.playerNotifications.data.categoriesWithCounters;
export const selectNotificationsByCategory = (state: { playerNotifications: PlayerNotificationsState; }) => state.playerNotifications.data.notificationsByCategory;
export const selectPlayerNotificationsLoading = (state: { playerNotifications: PlayerNotificationsState; }) => state.playerNotifications.loading;
export const selectPlayerNotificationsError = (state: { playerNotifications: PlayerNotificationsState; }) => state.playerNotifications.error;
export const selectPlayerNotificationsLastFetched = (state: { playerNotifications: PlayerNotificationsState; }) => state.playerNotifications.lastFetched;

// Convenience selector for navbar - returns just the unread count number
export const selectNotificationsUnreadCount = (state: RootState) => state.playerNotifications.data.unreadCount || 0;

export default playerNotificationsSlice.reducer;
